<template>
  <div id="organisation-screen">
    <OrganisationPanel
      :organisation-id="$route.params.organisationId"
      :business-id="$route.params.businessId"
      :conversation-id="$route.params.conversationId" />
    <router-view
      class="draggable"
      :class="{ 'full-window': fullWindow }" />

    <b-toast
      id="network-status-toast"
      toaster="b-toaster-bottom-left"
      toast-class="sb-toast hide-header"
      append-toast
      no-auto-hide
      @hide="hideOnlineToast"
      @show="showOnlineToast" >
      <div class="flex flex-align-center">
        <template v-if="online">
          <span class="emoji-fix">🎉</span> Your Internet connection was restored
        </template>
        <template v-else>
          <span class="emoji-fix">😵</span> You are currently offline
        </template>
        <SBIcon
          class="close-btn mr-0"
          :icon="SBIconList.CLOSE_THIN"
          color="#8E9BA2"
          :hover-icon-color="SBColors.greyUltralight"
          :size="10"
          :hover-size="25"
          :border-radius="100"
          :pointer="true"
          @click.native.stop="hideNetworkInfo()" />
      </div>
    </b-toast>

    <Activation
      v-if="!ignoreActivation && (activateOrganisation || reactivateOrganisation || isPermissionDenied)"
      :activation="activateOrganisation" />
  </div>
</template>

<script>
import '@/assets/css/modules/_merchant-dashboard.scss';
import { pushNotifications, app, notification } from '@todesktop/client-core';
import { isDesktopApp } from '@todesktop/client-core/platform/todesktop';

import store from '@/store';
import { mapActions, mapGetters } from 'vuex';
import Mouse from '@/helpers/Mouse';
import ToasterMixin from '@/mixins/Toaster';
import { getLastOrgId } from '@/helpers/localStorage.helpers';
import Activation from '@/components/auth/Activation';
import { loadOrganisationData } from '@/helpers/initializeOrganisationData';
import { injectScript } from '@/helpers/scriptInjection';
import JuneMixin from '@/mixins/June';
import OrganisationPanel from './OrganisationPanel';
import BackgroundActionsMixin from './mixins/BackgroundActions.mixin.vue';

let sleepWatchWorker;

function loadOrganisation(organisationId) {
  let returnValue = false;
  if (store.state.organisation.organisationList.find(org => org.id === organisationId)) {
    returnValue = new Promise((resolve) => {
      resolve(true);
    });
  } else {
    returnValue = store.dispatch('organisation/fetchOrganisations')
      .then(() => {
        store.dispatch('organisation/splashProgressIncrease');
        return true;
      })
      .catch((error) => {
        throw error;
      });
  }
  return returnValue;
}

// Preloading images for places where we are not rendering the elements right away. Like here in the organisation dropdown list. --> TODO: This shold probably be exported to some utils class.
async function preloadOrganisationImages() {
  store.getters['organisation/organisationList'].forEach((organisation) => {
    const res = document.createElement('link');
    res.rel = 'preload';
    res.as = 'image';
    res.href = organisation.logo;
    document.head.appendChild(res);
  });
}

export default {
  name: 'Organisation',
  components: {
    OrganisationPanel,
    Activation,
  },
  mixins: [
    ToasterMixin,
    JuneMixin,
    BackgroundActionsMixin,
  ],
  beforeRouteEnter(to, from, next) {
    if (store.getters['organisation/organisationList'].length > 0 && (from.meta.skipOrgCheck || to.meta.skipOrgCheck)) {
      next();
    } else {
      // check org and preload if needed
      store.commit('organisation/SET_SHOW_SPLASH');
      store.commit('organisation/SET_SPLASH_MESSAGE', 'Getting everything ready...');
      const orgIdToRedirectTo = getLastOrgId();
      loadOrganisation(to.params.organisationId || orgIdToRedirectTo)
        .then(() => {
          if (store.getters['organisation/organisationList'].length === 0) {
            next({ name: 'NoOrganisation' });
          } else if (store.getters['organisation/organisationList'].length > 0 && store.getters['organisation/organisationList'].find(org => org.id === to.params.organisationId || org.id === orgIdToRedirectTo)) {
            store.commit('organisation/CURRENT_ORGANISATION_SET_DATA', store.getters['organisation/organisationList'].find(org => org.id === to.params.organisationId || org.id === orgIdToRedirectTo));
            preloadOrganisationImages();
            next();
          } else {
            next({ name: 'Organisation' });
          }
        })
        .catch(error => console.log('error', error));
    }
  },
  props: {
    // organisationId: {
    //   type: String,
    //   required: true,
    // },
    // businessId: {
    //   type: String,
    //   required: false,
    //   default: '',
    // },
  },
  data() {
    return {
      initialized: false,
      online: true,
      onlineToastShown: false,
      statsRunning: -1,
    };
  },
  computed: {
    ...mapGetters({
      // merchantHasWhatsapp: 'merchant/merchantHasWhatsapp',
      currentOrganisation: 'organisation/currentOrganisation',
      organisationList: 'organisation/organisationList',
      incognito: 'user/incognito',
      reconnectChannel: 'pusher/reconnectChannel',
      forceResync: 'organisation/forceResync',
      reloadOrganisationList: 'organisation/reloadOrganisationList',
      organisationListLastUpdateTime: 'organisation/organisationListLastUpdateTime',
      onesignalAppId: 'system/onesignalAppId',
      statistics: 'organisation/statistics',
      permissionDenied: 'user/permissionDenied',
      currentOrganisationBusinessIds: 'organisation/currentOrganisationBusinessIds',
      fullWindow: 'system/fullWindow',
      me: 'organisation/me',
      readUpdate: 'pusher/readUpdate',
      organisationStatistics: 'organisation/statistics',
      unreadCounter: 'chat/unreadCounter',
    }),
    activateOrganisation() {
      return this.currentOrganisation && this.currentOrganisation.should_activate_account;
    },
    reactivateOrganisation() {
      return this.currentOrganisation && this.currentOrganisation.should_reactivate_account;
    },
    ignoreActivation() {
      return !!(this.incognito || (this.$route && this.$route.meta && this.$route.meta.ignoreActivation));
    },
    isPermissionDenied() {
      return this.permissionDenied.message === 'Unauthorized IP - Access Denied';
    },
  },
  watch: {
    // reload org if route changes
    $route(to, from) {
      if (to.params.organisationId && to.params.organisationId !== from.params.organisationId) {
        if (this.organisationListLastUpdateTime + 10000 > Date.now()) {
          // Take organisation from organisation list
          const targetOrganisation = this.organisationList.find(o => o.id === to.params.organisationId);
          this.setCurrentOrganisation(targetOrganisation);
        } else {
          // reload organisation list and then use it
          this.fetchOrganisations()
            .then((response) => {
              if (response.status === 200) {
                const targetOrganisation = response.data.find(o => o.id === to.params.organisationId);
                this.setCurrentOrganisation(targetOrganisation);
              }
            });
        }
      }
      if (['OrganisationConversation', 'OrganisationWithId'].includes(this.$route.name)) {
        this.setCurrentNumber(null);
      } else {
        this.setCurrentNumber(to.params.businessId);
      }
    },
    isPermissionDenied() {
      this.leaveOrganisationChannel(this.currentOrganisation.id);
      this.leaveFocusChannel(this.currentOrganisation.id);
    },
    currentOrganisation(newOrganisation, previousOrganisation) {
      this.resetAccess();
      if (
        newOrganisation &&
        previousOrganisation &&
        newOrganisation.id !== previousOrganisation.id
      ) {
        this.leaveOrganisationChannel(previousOrganisation.id);
        if (previousOrganisation && previousOrganisation.businesses && previousOrganisation.businesses.length) {
          previousOrganisation.businesses.forEach(number => this.leaveNumberChannel(number.id));
        }
      }

      this.joinOrganisationChannel(newOrganisation.id);
      this.joinFocusChannel({
        join: newOrganisation.id,
        checkPrevious: previousOrganisation.id,
      });

      // Connect to all of the pusher channels on the numbers in the organisation
      if (newOrganisation && newOrganisation.businesses && newOrganisation.businesses.length) {
        newOrganisation.businesses.forEach(number => this.joinNumberChannel(number.id));
      }

      if (this.currentOrganisation && (this.currentOrganisation.should_activate_account || this.currentOrganisation.should_reactivate_account)) {
        this.finalizeSplashView();
        return;
      }
      // Load organisation integrations data
      store.dispatch('organisation/loadOrganisationIntegrationsData');
      store.dispatch('organisation/fetchIntegrationsRegistry');
      store.dispatch('organisation/fetchChannelsRegistry');

      // disconnect pusher from old organisation merchants
      previousOrganisation.businesses.forEach((business) => {
        // this.turnOffMerchantEventListener(business.id);
      });
      // TODO clear all previous permissions
      // this.clearPermissions();

      // Connect to pusher for each branch(merchant)
      // Also fetch statistics and permissions for each merchant
      // this.fetchPermissionRoles();
      // TODO - move this to initializeOrganisation helper
      // this.fetchCustomFields({
      //   silent: true,
      //   organisationId: newOrganisation.id || getLastOrgId(),
      // });

      loadOrganisationData()
        .then(() => {
          this.finalizeSplashView();
        });

      if (
        !this.initialized ||
        (newOrganisation && previousOrganisation && newOrganisation.id !== previousOrganisation.id)
      ) {
        this.fetchFeatures();
        this.clearStatistics();
        const organisationId = newOrganisation.id || getLastOrgId();
        this.loadNumberStatistics(organisationId);

        this.initialized = true;
      }

      // check for system messages
      this.fetchNewMessages().then((response) => {
        if (response && response.status === 200) {
          response.data.forEach((message) => {
            if (message.action === 'notice') {
              this.showSystemMessage(message, this.SBIconList.NOTIFICATION_BELL, null);
            }
            if (message.action === 'restart') {
              // Ignore for app loading scenario
              // this.showSystemMessage(message, this.SBIconList.NOTIFICATION_BELL, null, { text: 'REFRESH' });
              // Only write it to local storage so it newer gets shown again because user is already on the newest app version
              this.addSystemMessageIdToLocalStorage(message.id);
            }
          });
          this.removeSystemMessageIdFromLocalStorage(response.data.map(message => message.id));
        }
      });
    },
    reconnectChannel() {
      // Do not trigger on initial connect
      if (this.reconnectChannel > 1) {
        const organisationId = this.currentOrganisation.id || getLastOrgId();
        this.loadNumberStatistics(organisationId);
      }
    },
    forceResync() {
      const organisationId = this.currentOrganisation.id || getLastOrgId();
      this.loadNumberStatistics(organisationId);
    },
    reloadOrganisationList() {
      this.reloadOrganisationListAction();
    },
    onesignalAppId: {
      handler() {
        if (!window.OneSignal) {
          injectScript('OneSignal', 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js', true).then(() => this.configureOneSignal());
        } else {
          this.configureOneSignal();
        }
      },
      immediate: true,
    },
    unreadCounter: {
      handler() {
        let counter = 0;
        try {
          Object.keys(this.unreadCounter).forEach((key) => {
            counter += this.unreadCounter[key].counter;
          });
        } catch (error) { /* do nothing */ }

        const countText = counter ? `(${this.$options.filters.nineNinePlus(counter)}) ` : '';
        const currentTitleWithoutCount = document.title.replace(/\(\d+\+?\)\s/, '');
        document.title = `${countText}${currentTitleWithoutCount}`;
      },
      immediate: true,
      deep: true,
    },
    readUpdate() {
      // skipp this on pages that use OrganisationChat component and have this logic implemented there
      if (['OrganisationWithId', 'OrganisationConversation', 'OrganisationBusinessConversation', 'OrganisationBusiness'].includes(this.$route.name)) {
        return;
      }
      const myConversation = this.readUpdate.conversation && this.readUpdate.conversation.assigned_user_id === this.me.id;
      if (this.readUpdate.conversation.seen) {
        let counter = this.organisationStatistics[this.readUpdate.business_uid].open.all_unread;
        let userCounter = this.organisationStatistics[this.readUpdate.business_uid].open.user_unread[this.me.id];
        counter = counter > 0 ? --counter : 0;
        if (myConversation) {
          userCounter = userCounter > 0 ? --userCounter : 0;
        }
        if (!myConversation) {
          this.$nextTick(() => {
            this.decrementStatisticLocalData(
              {
                businessId: this.readUpdate.business_uid,
                conversationId: this.readUpdate.conversation.id,
                myChat: this.readUpdate.conversation.assigned_user_id === this.me.id,
              },
            );
          });
        }
        if (this.readUpdate.conversation.assignment_seen) {
          this.setNumberUnreadCounter({
            numberId: this.readUpdate.business_uid,
            userCounter,
            counter,
          });
        }
      }
    },
  },
  beforeMount() {
    if (
      !this.initialized &&
      ['OrganisationConversation', 'OrganisationWithId'].includes(this.$route.name)
    ) {
      if (this.$route.params.businessId) {
        if (!this.$route.params.conversationId) {
          this.$router.push({
            name: 'OrganisationBusiness',
            params: {
              organisationId: this.$route.params.organisationId,
              businessId: this.$route.params.businessId,
            },
          }).catch(() => {});
        } else {
          this.$router.push({
            name: 'OrganisationBusinessConversation',
            params: {
              organisationId: this.$route.params.organisationId,
              businessId: this.$route.params.businessId,
              conversationId: this.$route.params.conversationId,
            },
          }).catch(() => {});
        }
      } else {
        this.$router.push({
          name: 'OrganisationWithId',
          params: {
            organisationId: this.$route.params.organisationId,
          },
        }).catch(() => {});
      }
    }
  },
  mounted() {
    Mouse.monitor();
    if (this.$route.params.organisationId && this.organisationList.find(org => org.id === this.$route.params.organisationId)) {
      if (this.organisationListLastUpdateTime + 10000 > Date.now()) {
        // Take organisation from organisation list
        const targetOrganisation = this.organisationList.find(o => o.id === this.$route.params.organisationId);
        this.setCurrentOrganisation(targetOrganisation);
      } else {
        // reload organisation list and then use it
        this.fetchOrganisations()
          .then((response) => {
            if (response.status === 200) {
              const targetOrganisation = response.data.find(o => o.id === this.$route.params.organisationId);
              this.setCurrentOrganisation(targetOrganisation);
            }
          });
      }
    }
    // TODO IMPROVE ICON MANIPULATION - not ready on backend yet
    window.eventBus.$on('notice', data => this.showSystemMessage(data, this.SBIconList.NOTIFICATION_BELL, null));
    window.eventBus.$on('restart', data => this.showSystemMessage(data, null, null, { text: 'REFRESH' }, '🎁'));
    window.eventBus.$on('show-toast', data => this.showToast(data));

    window.addEventListener('focus', this.onFocus);
    window.addEventListener('blur', this.onBlur);
    window.addEventListener('offline', this.onOffline);
    window.addEventListener('online', this.onOnline);

    if (window.Worker) {
      if (sleepWatchWorker) {
        sleepWatchWorker.terminate();
      }
      // One of the three options to detect when to resync the app. #resync
      sleepWatchWorker = new Worker('/static/workers/Sleep.worker.js');
      sleepWatchWorker.onmessage = (ev) => {
        if (ev && ev.data === 'wakeup') {
          window.eventBus.$emit('wakeup');
          this.resetPusher();
          // this.forceResyncAction();
          this.hideNetworkInfo();
          this.fetchNewMessages().then((response) => {
            if (response && response.status === 200) {
              response.data.forEach((message) => {
                if (message.action === 'notice') {
                  this.showSystemMessage(message, this.SBIconList.NOTIFICATION_BELL, null);
                }
                if (message.action === 'restart') {
                  this.showSystemMessage(message, this.SBIconList.NOTIFICATION_BELL, null, { text: 'REFRESH' });
                }
              });
              this.removeSystemMessageIdFromLocalStorage(response.data.map(message => message.id));
            }
          });
        }
      };
    }

    if (this.currentOrganisation && (this.currentOrganisation.should_activate_account || this.currentOrganisation.should_reactivate_account)) {
      this.finalizeSplashView();
    }
    if (this.currentOrganisation && this.currentOrganisation.id) {
      setTimeout(() => {
        this.joinOrganisationChannel(this.currentOrganisation.id);
        this.joinFocusChannel({
          join: this.currentOrganisation.id,
        });
      }, 2000);
    }

    this.joinPrivateChannel();

    this.$nextTick(() => {
      // Trigger app install June event only once
      const desktopAppInstalled = localStorage.getItem('desktop_app_installed');
      if (window.todesktop && !desktopAppInstalled) {
        this.juneTrack('New Desktop app install', { ...window.todesktop.os });
        localStorage.setItem('desktop_app_installed', true);
      }

      if (window.todesktop) {
        this.juneTrack('Logged in to Desktop app', { ...window.todesktop.os });
      }
    });
  },
  beforeDestroy() {
    // this.$store.commit('resetMerchant');
    window.removeEventListener('focus', this.onFocus);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('offline', this.onOffline);
    window.removeEventListener('online', this.onOnline);
    this.leavePrivateChannel();
    this.leaveFocusChannel(this.currentOrganisation.id);
  },
  methods: {
    ...mapActions({
      fetchOrganisations: 'organisation/fetchOrganisations',
      fetchCurrentOrganisation: 'organisation/fetchCurrentOrganisation',
      setCurrentOrganisation: 'organisation/setCurrentOrganisation',
      // fetchOrganisationMerchants: 'merchant/fetchOrganisationMerchants',
      // turnOffMerchantEventListener: 'pusher/turnOffMerchantEventListener',
      fetchPermissionRoles: 'merchant/fetchPermissionRoles',
      splashProgressIncrease: 'organisation/splashProgressIncrease',
      finalizeSplashView: 'organisation/finalizeSplashView',
      joinOrganisationChannel: 'pusher/joinOrganisationChannel',
      joinNumberChannel: 'pusher/joinNumberChannel',
      leaveOrganisationChannel: 'pusher/leaveOrganisationChannel',
      leaveNumberChannel: 'pusher/leaveNumberChannel',
      joinPrivateChannel: 'pusher/joinPrivateChannel',
      leavePrivateChannel: 'pusher/leavePrivateChannel',
      getInboxStats: 'inbox/getInboxStats',
      clearStatistics: 'organisation/clearStatistics',
      fetchOrganisationStatistics: 'organisation/fetchOrganisationStatistics',
      fetchNewMessages: 'system/fetchNewMessages',
      fetchCustomFields: 'contact/fetchCustomFields20',
      setCurrentNumber: 'organisation/setCurrentNumber',
      forceResyncAction: 'organisation/forceResync',
      reloadOrganisationListAction: 'organisation/reloadOrganisationList',
      setOneSignalId: 'user/setOneSignalId',
      removeOneSignalId: 'user/removeOneSignalId',
      joinFocusChannel: 'pusher/joinFocusChannel',
      leaveFocusChannel: 'pusher/leaveFocusChannel',
      resetAccess: 'user/resetAccess',
      fetchFeatures: 'organisation/fetchFeatures',
      setUnreadCounter: 'organisation/setUnreadCounter',
      resetPusher: 'pusher/resetPusher',
      setNumberUnreadCounter: 'organisation/setNumberUnreadCounter',
      decrementStatisticLocalData: 'organisation/decrementStatisticLocalData',
    }),
    configureOneSignal() {
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      const OneSignalDeferred = window.OneSignalDeferred;
      // ToDesktop app doesnt need OneSignal and the permission promt should not be shown
      if (!isDesktopApp()) {
        OneSignalDeferred.push(async (OneSignal) => {
          // OneSignal.Debug.setLogLevel('trace');
          // console.log(this.onesignalAppId);
          await OneSignal.init({
            // appId: 'ac51e6d6-600f-48d1-aa0d-cec9b248ea1a', // TEST onesignal app that works on localhost
            appId: this.onesignalAppId,
          })
            .then(() => {
              // console.log(OneSignal.User.PushSubscription.optedIn);
              const externalId = localStorage.getItem('userId');
              if (!externalId) {
                console.error('No external id found');
                return;
              }
              OneSignal.login(externalId).then(() => {
                if (OneSignal.User.PushSubscription.id) {
                  OneSignal.User.addTag('email', this.me.email);
                  this.setOneSignalId({ onesignal_uid: OneSignal.User.PushSubscription.id });
                }
              });

              OneSignal.User.PushSubscription.addEventListener('change', (subscription) => {
                const oneSignalSubscriptionId = subscription.current.id;
                if (!oneSignalSubscriptionId) {
                  return;
                }
                if (subscription.current.optedIn) {
                  OneSignal.login(externalId).then(() => {
                    OneSignal.User.addTag('email', this.me.email);
                  });
                  // OneSignal.User.addTag('email', this.me.email);
                  // OneSignal.User.addEmail(this.me.email); // NOTE: this creates new mail subscription
                  this.setOneSignalId({ onesignal_uid: oneSignalSubscriptionId });
                } else {
                  this.removeOneSignalId(oneSignalSubscriptionId);
                }
              });
            });
        });
      }
    },
    loadNumberStatistics(organisationId, chunkCount = 0) {
      if (
        (organisationId !== this.currentOrganisation.id) ||
        (this.statsRunning >= chunkCount)
      ) {
        return;
      }
      this.statsRunning = chunkCount;
      const perChunk = 10;
      const chunk = this.currentOrganisationBusinessIds.slice(chunkCount * perChunk, chunkCount * perChunk + perChunk);
      if (chunk.length) {
        this.fetchOrganisationStatistics({
          organisationId: this.currentOrganisation.id || getLastOrgId(),
          forceStatistics: true,
          data: {
            business_uids: chunk.join(','),
            sections: 'all_unread,user_unread',
          },
        })
          .then((response) => {
            if (response.status === 200) {
              this.loadNumberStatistics(organisationId, chunkCount + 1);
            }
          });
      } else {
        setTimeout(() => {
          this.statsRunning = -1;
        }, 5000);
      }
    },
    onBlur() {
      this.leaveFocusChannel(this.currentOrganisation.id);
    },
    onFocus() {
      this.joinOrganisationChannel(this.$route.params.organisationId);
      this.joinFocusChannel({
        join: this.currentOrganisation.id,
      });
    },
    onOffline() {
      // console.log('OFFline');
      this.online = false;
      window.sbToast.show('network-status-toast');
    },
    onOnline() {
      // console.log('ONline', this.online);
      if (this.online) { // To prevent initial online event. Offline event shoul come before the online event triggers message toast.
        return;
      }
      this.online = true;
      if (!this.onlineToastShown) {
        window.sbToast.show('network-status-toast');
      }
      // console.log('C3s');
      setTimeout(this.hideNetworkInfo, 3000);
      // One of the three options to detect when to resync the app. #resync
      this.forceResyncAction();
    },
    showToast(data) {
      this.showToaster(data.message, null, null, data.variant);
    },
    hideNetworkInfo() {
      window.sbToast.hide('network-status-toast');
    },
    hideOnlineToast() {
      this.onlineToastShown = false;
    },
    showOnlineToast() {
      this.onlineToastShown = true;
    },
  },
};
</script>

<style lang="scss" scoped>
#organisation-screen {
  display: flex;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  .full-window {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    z-index: 1039;
  }
}
</style>
